import axios from "axios";
import { uuid } from "kdc-shared";
import { req, kecapi } from "./req";
import type { KscResponseModel } from "../../../common/core/request";
import type { Param } from "@kce2/common/api/types";
import type {
  AccountProject,
  AddExistedNodesParam,
  AddExistedNodesResponse,
  BatchCalculateKVMPriceParam,
  BatchCalculateKVMPriceResponse,
  CalculateKVMPriceParam,
  CalculateKVMPriceResponse,
  CalculateProductPriceParam,
  CalculateProductPriceResponse,
  DescribeAllVpcInfoParam,
  DescribeAllVpcInfoResponse,
  DescribeEIPParam,
  DescribeEIPResponse,
  DescribeExistedInstancesParam,
  DescribeExistedInstancesResponse,
  DescribeInstanceGroupCountParam,
  DescribeInstanceGroupCountResponse,
  DescribeKecRegionListParam,
  DescribeKecRegionListResponse,
  DescribeKeysResponse,
  DescribeKlogProjectListParm,
  DescribeKlogProjectListResponse,
  DescribeManagedClusterSpecsParam,
  DescribeManagedClusterSpecsResponse,
  DescribeProductSellOutsParam,
  DescribeProductSellOutsResponse,
  DescribeQuotasParam,
  DescribeQuotasResponse,
  DescribeRoutesParam,
  DescribeRoutesResponse,
  GetAccountAllProjectListParam,
  GetAccountAllProjectListResponse,
  KlogUserStatusParam,
  KlogUserStatusResponse,
  QueryKvmListParam,
  QueryKvmListResponse,
  QueryProductDetailParam,
  QueryProductDetailResponse,
  QueryProductPriceParam,
  QueryProductPriceResponse,
  ValidateIpv6SupportResponse,
  VerifyKceRoleParam,
  VerifyKceRoleResponse,
} from "./types";

// https://kec.console.ksyun.com/kecapi/?Action=RegionInfos&Service=kec&Version=2016-03-04&source=kec&Region=cn-beijing-6

export async function addExistedNodes(params: AddExistedNodesParam) {
  return axios<AddExistedNodesResponse>(
    req.post("AddExistedNodes", params.Region, {
      params: {},
    }),
  );
}

export async function describeQuotas(params: DescribeQuotasParam) {
  return axios<KscResponseModel<DescribeQuotasResponse>>(
    req.get("DescribeQuotas", params.Region, {
      params: params,
    }),
  );
}

export async function describeRoutes(params: DescribeRoutesParam) {
  return axios<KscResponseModel<DescribeRoutesResponse>>(
    req.get("DescribeRoutes", params.Region, {
      params,
    }),
  );
}

export async function klogUserStatus(params: KlogUserStatusParam) {
  return axios<KlogUserStatusResponse>(
    req.get("KlogUserStatus", params.Region, {
      params: {
        ...params,
      },
    }),
  );
}

export async function describeKlogProjectList(params: DescribeKlogProjectListParm) {
  return axios<DescribeKlogProjectListResponse>(
    req.get("DescribeKlogProjectList", params.Region, {
      params: {},
    }),
  );
}

export async function describeAllVpcInfo(params: DescribeAllVpcInfoParam) {
  return axios<DescribeAllVpcInfoResponse>(
    req.get("AllVpcInfo", params.Region, {
      params: { Service: "vpc" },
      headers: {
        "X-KSC-REQUEST-ID": uuid(),
      },
    }),
  );
}

export async function describeKecRegionList(params: DescribeKecRegionListParam) {
  return axios<DescribeKecRegionListResponse>(kecapi.get("RegionInfos", params.Region, {}));
}

export async function queryKvmList(params: QueryKvmListParam) {
  return axios<QueryKvmListResponse>(kecapi.get("QueryKvmList", params.Region, { params: params }));
}

export async function describeEip(params: DescribeEIPParam) {
  return axios<KscResponseModel<DescribeEIPResponse>>(
    req.post("DescribeEIP", params.Region, {
      data: params,
    }),
  );
}

export async function describeProductSellOuts(params: DescribeProductSellOutsParam) {
  return axios<DescribeProductSellOutsResponse>(
    req.get("DescribeProductSellOuts", params.Region, {
      params,
    }),
  );
}

export async function queryProductPrice(params: QueryProductPriceParam) {
  return axios<QueryProductPriceResponse>(
    req.get("QueryProductPrice", params.Region, {
      params: {
        ProductTypeId: 151,
        ...params,
      },
    }),
  );
}

export async function queryProductDetail(params: QueryProductDetailParam) {
  return axios<KscResponseModel<QueryProductDetailResponse>>(
    req.get("QueryProductDetail", params.Region, {
      params,
    }),
  );
}

export async function describeManagedClusterSpecs(params?: DescribeManagedClusterSpecsParam) {
  return axios<KscResponseModel<DescribeManagedClusterSpecsResponse>>(
    req.get("DescribeManagedClusterSpecs", params?.Region, {
      params,
    }),
  );
}

export async function calculateProductParam(params: CalculateProductPriceParam) {
  return axios<KscResponseModel<CalculateProductPriceResponse>>(
    req.post("CalculateProductParam", params.Region, {
      body: params,
    }),
  );
}

export async function verifyKceRole(params: VerifyKceRoleParam) {
  return axios<VerifyKceRoleResponse>(
    req.get("VerifyKceRole", params.Region, {
      params,
    }),
  );
}

export async function getAccountAllProjectList(params: GetAccountAllProjectListParam) {
  return axios<KscResponseModel<GetAccountAllProjectListResponse>>(
    req.get("GetAccountAllProjectList", params.Region, {
      params: {
        type: params.Type,
      },
    }),
  );
}

export async function getProjList(params: { Region: string; Type: string }) {
  return axios<{
    ListProjectResult: {
      Total: number;
      ProjectList: AccountProject[];
    };
    RequestId: string;
  }>(
    req.get("GetAccountProjectList", params.Region, {
      params: {
        Service: "iam",
        Version: "2015-11-01",
        source: "kec",
        type: params.Type,
      },
    }),
  );
}

export async function describeSystemLabels(params: Param) {
  return axios<KscResponseModel<{ LabelName: string; LabelValue: string }[]>>(
    req.get("DescribeNodeSysLabel", params.Region),
  );
}

export async function describeKeys(params: Param) {
  return axios<DescribeKeysResponse>(
    req.post("DescribeKeys", params.Region, {
      params: {
        Service: "sks",
        Version: "2015-11-01",
        source: "kec",
      },
      body: {
        MaxResults: 500,
      },
    }),
  );
}

export async function describeExistedInstances(params: DescribeExistedInstancesParam) {
  return axios<KscResponseModel<DescribeExistedInstancesResponse>>(
    req.get("DescribeExistedInstances", params.Region, {
      params: params,
    }),
  );
}

export async function validateIpv6Support(params: Param) {
  return axios<ValidateIpv6SupportResponse>(kecapi.get("ValidateIpv6Support", params.Region));
}

export async function describeInstanceGroupCount(params: DescribeInstanceGroupCountParam) {
  return axios<DescribeInstanceGroupCountResponse>(
    kecapi.post("DescribeInstanceGroupCount", params.Region, {
      data: { ProjectIds: params.ProjectIds },
    }),
  );
}

export async function batchCalculateKVMPrice(params: BatchCalculateKVMPriceParam) {
  return axios<BatchCalculateKVMPriceResponse>(
    kecapi.post("BatchCalculateKVMPrice", params.Region, {
      data: params.Data,
    }),
  );
}

export async function calculateKVMPrice(params: CalculateKVMPriceParam) {
  return axios<CalculateKVMPriceResponse>(
    kecapi.post("CalculateKVMPrice", params.Region, {
      data: params,
    }),
  );
}

// TODO: 后续会将相关的接口陆续迁移到对应的文件中，并使用新的接口书写方式
export * from "./cluster";
export * from "./addon";
export * from "./node";
export * from "./node-pool";
export * from "./envoy-gateway";
export * from "./k8s-resource/cluster-resource-proxy";
export * from "./k8s-resource/ingress";
export * from "./k8s-resource/secret";
